/**
 * SimulationEngine — deterministic PVI graph execution simulator.
 *
 * Traverses the PVI DAG in topological order, evaluating each node's
 * value operation and updating balances. Mirrors real Rivellum execution
 * semantics: UVL constraints are respected, value is conserved, and
 * no operation can execute with insufficient balance.
 *
 * This is NOT mock execution — every step reflects what would happen on-chain.
 */

import type {
  NodeId,
  PviNodeType,
  PviNodeWithSpan,
  PviGraphWithSpans,
  Condition,
} from './pviTypes';

/* ================================================================== */
/*  Types                                                             */
/* ================================================================== */

export type SimNodeStatus = 'idle' | 'active' | 'completed' | 'failed' | 'skipped';

export interface SimulationInput {
  name: string;
  type: 'number' | 'address' | 'asset' | 'boolean';
  required: boolean;
  defaultValue: string;
  /** Which node(s) this input feeds into. */
  nodeIds: NodeId[];
}

/** Per-address, per-asset balance. */
export interface BalanceMap {
  [address: string]: { [asset: string]: number };
}

/** Snapshot of a single balance change. */
export interface BalanceChange {
  address: string;
  asset: string;
  before: number;
  after: number;
}

export interface ExecutionStep {
  index: number;
  nodeId: NodeId;
  nodeType: PviNodeType;
  operation: string;
  balancesBefore: BalanceMap;
  balancesAfter: BalanceMap;
  changes: BalanceChange[];
  /** Edges that were traversed in this step. */
  edgeIds: number[];
  status: 'success' | 'error';
  error: string | null;
  /** Simulated block timestamp for this step. */
  timestamp: number;
}

export interface SimulationResult {
  success: boolean;
  steps: ExecutionStep[];
  finalBalances: BalanceMap;
  errors: string[];
  warnings: string[];
  /** Total value moved across all steps. */
  totalValueMoved: number;
}

export interface SimulationInputValues {
  [name: string]: string;
}

/* ================================================================== */
/*  Input Generation                                                  */
/* ================================================================== */

/**
 * Derive required simulation inputs from the PVI graph.
 * Scans nodes for addresses, amounts, and assets referenced.
 */
export function deriveInputs(graph: PviGraphWithSpans): SimulationInput[] {
  const inputs: SimulationInput[] = [];
  const seenAddresses = new Set<string>();
  const seenAssets = new Set<string>();
  const hasAmount = new Set<NodeId>();

  for (const node of graph.nodes) {
    // Collect addresses
    if (node.party.type === 'account' && !seenAddresses.has(node.party.address)) {
      seenAddresses.add(node.party.address);
      inputs.push({
        name: node.party.address,
        type: 'address',
        required: true,
        defaultValue: node.party.address,
        nodeIds: [node.id],
      });
    }

    // Collect assets
    if (node.assetId && !seenAssets.has(node.assetId)) {
      seenAssets.add(node.assetId);
    }

    // Amounts that need input
    if (node.amount !== '0' && (node.nodeType === 'HOLD' || node.nodeType === 'PAY' || node.nodeType === 'MINT')) {
      hasAmount.add(node.id);
    }

    // Conditions that need user input
    for (const cond of node.conditions) {
      if (cond.type === 'time_absolute') {
        const name = `block_height`;
        if (!inputs.find(i => i.name === name)) {
          inputs.push({
            name,
            type: 'number',
            required: false,
            defaultValue: String(cond.after_height + 1),
            nodeIds: [node.id],
          });
        }
      }
      if (cond.type === 'oracle_threshold') {
        const name = `oracle_${cond.oracle_id}_${cond.field}`;
        if (!inputs.find(i => i.name === name)) {
          inputs.push({
            name,
            type: 'number',
            required: false,
            defaultValue: String(oracleDefaultForOp(cond.operator, cond.value)),
            nodeIds: [node.id],
          });
        }
      }
    }
  }

  // Add amount inputs for each asset
  for (const asset of seenAssets) {
    inputs.push({
      name: `initial_balance_${asset}`,
      type: 'number',
      required: true,
      defaultValue: '1000000',
      nodeIds: [],
    });
  }

  // If there are nodes with amounts but no specific asset tracked, add general amount
  if (seenAssets.size === 0 && hasAmount.size > 0) {
    inputs.push({
      name: 'initial_balance',
      type: 'number',
      required: true,
      defaultValue: '1000000',
      nodeIds: [],
    });
  }

  return inputs;
}

/** Pick a default oracle value that satisfies the given operator. */
export function oracleDefaultForOp(op: string, value: number): number {
  switch (op) {
    case 'gt': return value + 1;
    case 'ge': return value;
    case 'lt': return value - 1;
    case 'le': return value;
    case 'eq': return value;
    case 'ne': return value + 1;
    default:  return value + 1;
  }
}

/* ================================================================== */
/*  Topological Sort                                                  */
/* ================================================================== */

function topoSort(graph: PviGraphWithSpans): NodeId[] {
  const inDegree = new Map<NodeId, number>();
  const adj = new Map<NodeId, NodeId[]>();

  for (const n of graph.nodes) {
    inDegree.set(n.id, 0);
    adj.set(n.id, []);
  }
  for (const e of graph.edges) {
    inDegree.set(e.destination, (inDegree.get(e.destination) ?? 0) + 1);
    adj.get(e.source)?.push(e.destination);
  }

  // BFS (Kahn's algorithm)
  const queue: NodeId[] = [];
  for (const [id, deg] of inDegree) {
    if (deg === 0) queue.push(id);
  }

  const order: NodeId[] = [];
  while (queue.length > 0) {
    const nid = queue.shift()!;
    order.push(nid);
    for (const child of (adj.get(nid) ?? [])) {
      const newDeg = (inDegree.get(child) ?? 1) - 1;
      inDegree.set(child, newDeg);
      if (newDeg === 0) queue.push(child);
    }
  }

  // If graph has cycles (shouldn't for valid PVI), append remaining
  if (order.length < graph.nodes.length) {
    for (const n of graph.nodes) {
      if (!order.includes(n.id)) order.push(n.id);
    }
  }

  return order;
}

/* ================================================================== */
/*  Balance Helpers                                                   */
/* ================================================================== */

function cloneBalances(b: BalanceMap): BalanceMap {
  const out: BalanceMap = {};
  for (const addr of Object.keys(b)) {
    out[addr] = { ...b[addr] };
  }
  return out;
}

function getBalance(balances: BalanceMap, address: string, asset: string): number {
  return balances[address]?.[asset] ?? 0;
}

function setBalance(balances: BalanceMap, address: string, asset: string, amount: number): void {
  if (!balances[address]) balances[address] = {};
  balances[address][asset] = amount;
}

function adjustBalance(balances: BalanceMap, address: string, asset: string, delta: number): void {
  const current = getBalance(balances, address, asset);
  setBalance(balances, address, asset, current + delta);
}

/* ================================================================== */
/*  Condition Evaluation                                              */
/* ================================================================== */

function evaluateConditions(
  conditions: Condition[],
  inputValues: SimulationInputValues,
): { met: boolean; reason: string | null } {
  for (const cond of conditions) {
    switch (cond.type) {
      case 'time_absolute': {
        const height = parseInt(inputValues['block_height'] ?? '0', 10);
        if (height < cond.after_height) {
          return { met: false, reason: `Block height ${height} < required ${cond.after_height}` };
        }
        break;
      }
      case 'oracle_threshold': {
        const key = `oracle_${cond.oracle_id}_${cond.field}`;
        const raw = inputValues[key];
        if (raw === undefined) break; // No oracle input → condition passes (default)
        const oracleVal = parseFloat(raw);
        if (isNaN(oracleVal)) break;
        let passed = false;
        switch (cond.operator) {
          case 'gt': passed = oracleVal > cond.value; break;
          case 'ge': passed = oracleVal >= cond.value; break;
          case 'lt': passed = oracleVal < cond.value; break;
          case 'le': passed = oracleVal <= cond.value; break;
          case 'eq': passed = oracleVal === cond.value; break;
          case 'ne': passed = oracleVal !== cond.value; break;
        }
        if (!passed) {
          return { met: false, reason: `Oracle ${cond.oracle_id}.${cond.field} = ${oracleVal} does not satisfy ${cond.operator} ${cond.value}` };
        }
        break;
      }
    }
  }
  return { met: true, reason: null };
}

/* ================================================================== */
/*  Simulation Engine                                                 */
/* ================================================================== */

const ESCROW_ADDRESS = '__escrow__';

export function runSimulation(
  graph: PviGraphWithSpans,
  inputValues: SimulationInputValues,
): SimulationResult {
  const order = topoSort(graph);
  const nodeMap = new Map<NodeId, PviNodeWithSpan>(graph.nodes.map(n => [n.id, n]));

  // Initialize balances
  const balances: BalanceMap = {};
  const assets = new Set<string>();
  for (const n of graph.nodes) {
    if (n.assetId) assets.add(n.assetId);
  }

  // Seed initial balances from inputs
  const addresses = new Set<string>();
  for (const n of graph.nodes) {
    if (n.party.type === 'account') addresses.add(n.party.address);
  }

  for (const addr of addresses) {
    balances[addr] = {};
    for (const asset of assets) {
      const key = `initial_balance_${asset}`;
      const fallback = inputValues['initial_balance'] ?? '1000000';
      const amount = parseInt(inputValues[key] ?? fallback, 10);
      balances[addr][asset] = amount;
    }
    // Handle no-asset case
    if (assets.size === 0) {
      const amount = parseInt(inputValues['initial_balance'] ?? '1000000', 10);
      balances[addr]['default'] = amount;
    }
  }

  // Initialize escrow
  balances[ESCROW_ADDRESS] = {};

  const steps: ExecutionStep[] = [];
  const errors: string[] = [];
  const warnings: string[] = [];
  let totalValueMoved = 0;
  let failed = false;
  let simTime = 1000;

  for (const nodeId of order) {
    const node = nodeMap.get(nodeId);
    if (!node) continue;
    if (failed) break;

    const balancesBefore = cloneBalances(balances);
    const changes: BalanceChange[] = [];
    const edgeIds: number[] = [];
    let stepError: string | null = null;

    // Check conditions
    const condResult = evaluateConditions(node.conditions, inputValues);
    if (!condResult.met) {
      steps.push({
        index: steps.length,
        nodeId,
        nodeType: node.nodeType,
        operation: `${node.nodeType} — condition not met`,
        balancesBefore,
        balancesAfter: cloneBalances(balances),
        changes: [],
        edgeIds: [],
        status: 'error',
        error: condResult.reason,
        timestamp: simTime,
      });
      warnings.push(`Node #${nodeId}: ${condResult.reason}`);
      continue;
    }

    const asset = node.assetId || 'default';
    const rawAmount = parseFloat(node.amount);
    const isSymbolic = isNaN(rawAmount);
    const amount = isSymbolic ? 0 : rawAmount;

    // Resolve symbolic amounts: use initial_balance for the asset
    const resolvedAmount = isSymbolic
      ? parseInt(inputValues[`initial_balance_${asset}`] ?? inputValues['initial_balance'] ?? '1000000', 10)
      : rawAmount;

    // Get incoming edges
    const inEdges = graph.edges.filter(e => e.destination === nodeId);
    const outEdges = graph.edges.filter(e => e.source === nodeId);

    try {
      switch (node.nodeType) {
        case 'HOLD': {
          // Transfer value from sender to escrow
          const sender = node.party.type === 'account' ? node.party.address : Object.keys(balances).find(a => a !== ESCROW_ADDRESS) ?? 'unknown';
          const senderBal = getBalance(balances, sender, asset);
          const holdAmount = resolvedAmount;

          if (holdAmount > 0 && senderBal < holdAmount) {
            stepError = `Insufficient balance: ${sender} has ${senderBal} ${asset}, needs ${holdAmount}`;
            failed = true;
          } else {
            if (holdAmount > 0) {
              adjustBalance(balances, sender, asset, -holdAmount);
              adjustBalance(balances, ESCROW_ADDRESS, asset, holdAmount);
              changes.push(
                { address: sender, asset, before: senderBal, after: getBalance(balances, sender, asset) },
                { address: ESCROW_ADDRESS, asset, before: getBalance(balancesBefore, ESCROW_ADDRESS, asset), after: getBalance(balances, ESCROW_ADDRESS, asset) },
              );
              totalValueMoved += holdAmount;
            }
          }
          break;
        }

        case 'PAY': {
          // Transfer value to recipient
          const recipient = node.party.type === 'account' ? node.party.address : 'unknown';
          let payAmount = amount;

          // If incoming edge from HOLD/SPLIT, use escrow as source
          if (inEdges.length > 0) {
            const sourceEdge = inEdges[0];
            edgeIds.push(sourceEdge.id);

            // For split edges, calculate proportional amount
            if (sourceEdge.weightBp > 0) {
              const escrowBal = getBalance(balances, ESCROW_ADDRESS, asset);
              payAmount = Math.floor((escrowBal * sourceEdge.weightBp) / 10000);
            } else if (isSymbolic || payAmount === 0) {
              // Symbolic amount or vault reference — use full escrow balance
              payAmount = getBalance(balances, ESCROW_ADDRESS, asset);
            }

            const escrowBal = getBalance(balances, ESCROW_ADDRESS, asset);
            if (payAmount > 0 && escrowBal < payAmount) {
              stepError = `Insufficient escrow: has ${escrowBal} ${asset}, needs ${payAmount}`;
              failed = true;
            } else if (payAmount > 0) {
              adjustBalance(balances, ESCROW_ADDRESS, asset, -payAmount);
              adjustBalance(balances, recipient, asset, payAmount);
              changes.push(
                { address: ESCROW_ADDRESS, asset, before: escrowBal, after: getBalance(balances, ESCROW_ADDRESS, asset) },
                { address: recipient, asset, before: getBalance(balancesBefore, recipient, asset), after: getBalance(balances, recipient, asset) },
              );
              totalValueMoved += payAmount;
            }
          } else {
            // Direct pay from sender
            if (isSymbolic) payAmount = resolvedAmount;
            const senders = Object.keys(balances).filter(a => a !== ESCROW_ADDRESS && a !== recipient);
            const sender = senders[0] ?? 'unknown';
            const senderBal = getBalance(balances, sender, asset);

            if (payAmount > 0 && senderBal < payAmount) {
              stepError = `Insufficient balance: ${sender} has ${senderBal} ${asset}, needs ${payAmount}`;
              failed = true;
            } else if (payAmount > 0) {
              adjustBalance(balances, sender, asset, -payAmount);
              adjustBalance(balances, recipient, asset, payAmount);
              changes.push(
                { address: sender, asset, before: senderBal, after: getBalance(balances, sender, asset) },
                { address: recipient, asset, before: getBalance(balancesBefore, recipient, asset), after: getBalance(balances, recipient, asset) },
              );
              totalValueMoved += payAmount;
            }
          }
          break;
        }

        case 'RELEASE': {
          // Release value from escrow to recipient
          const recipient = node.party.type === 'account' ? node.party.address : 'unknown';
          for (const ie of inEdges) {
            edgeIds.push(ie.id);
          }

          const escrowBal = getBalance(balances, ESCROW_ADDRESS, asset);
          const releaseAmount = escrowBal; // Release all held value
          if (releaseAmount > 0) {
            adjustBalance(balances, ESCROW_ADDRESS, asset, -releaseAmount);
            adjustBalance(balances, recipient, asset, releaseAmount);
            changes.push(
              { address: ESCROW_ADDRESS, asset, before: escrowBal, after: getBalance(balances, ESCROW_ADDRESS, asset) },
              { address: recipient, asset, before: getBalance(balancesBefore, recipient, asset), after: getBalance(balances, recipient, asset) },
            );
            totalValueMoved += releaseAmount;
          }
          break;
        }

        case 'REFUND': {
          // Refund from escrow back to original sender
          const recipient = node.party.type === 'account' ? node.party.address : 'unknown';
          for (const ie of inEdges) {
            edgeIds.push(ie.id);
          }

          const escrowBal = getBalance(balances, ESCROW_ADDRESS, asset);
          if (escrowBal > 0) {
            adjustBalance(balances, ESCROW_ADDRESS, asset, -escrowBal);
            adjustBalance(balances, recipient, asset, escrowBal);
            changes.push(
              { address: ESCROW_ADDRESS, asset, before: escrowBal, after: 0 },
              { address: recipient, asset, before: getBalance(balancesBefore, recipient, asset), after: getBalance(balances, recipient, asset) },
            );
            totalValueMoved += escrowBal;
          }
          break;
        }

        case 'SPLIT': {
          // Split passes value through — actual distribution happens at PAY nodes
          for (const ie of inEdges) {
            edgeIds.push(ie.id);
          }
          for (const oe of outEdges) {
            edgeIds.push(oe.id);
          }
          // Validate split percentages
          const totalBp = outEdges.reduce((s, e) => s + e.weightBp, 0);
          if (outEdges.length > 0 && totalBp !== 10000) {
            warnings.push(`Node #${nodeId}: Split percentages sum to ${totalBp / 100}%, expected 100%`);
          }
          break;
        }

        case 'MINT': {
          // Create new value
          const mintAmount = resolvedAmount;
          if (mintAmount > 0) {
            adjustBalance(balances, ESCROW_ADDRESS, asset, mintAmount);
            changes.push(
              { address: ESCROW_ADDRESS, asset, before: getBalance(balancesBefore, ESCROW_ADDRESS, asset), after: getBalance(balances, ESCROW_ADDRESS, asset) },
            );
            totalValueMoved += mintAmount;
          }
          break;
        }

        case 'BURN': {
          // Destroy value from escrow
          for (const ie of inEdges) {
            edgeIds.push(ie.id);
          }
          const escrowBal = getBalance(balances, ESCROW_ADDRESS, asset);
          if (escrowBal > 0) {
            adjustBalance(balances, ESCROW_ADDRESS, asset, -escrowBal);
            changes.push(
              { address: ESCROW_ADDRESS, asset, before: escrowBal, after: 0 },
            );
          }
          break;
        }
      }
    } catch (err) {
      stepError = `Runtime error: ${err instanceof Error ? err.message : String(err)}`;
      failed = true;
    }

    steps.push({
      index: steps.length,
      nodeId,
      nodeType: node.nodeType,
      operation: `${node.nodeType}${resolvedAmount > 0 ? ` ${resolvedAmount} ${asset}` : ''}`,
      balancesBefore,
      balancesAfter: cloneBalances(balances),
      changes,
      edgeIds,
      status: stepError ? 'error' : 'success',
      error: stepError,
      timestamp: simTime,
    });

    if (stepError) {
      errors.push(`Step ${steps.length}: ${stepError}`);
    }

    simTime += 100;
  }

  return {
    success: !failed,
    steps,
    finalBalances: cloneBalances(balances),
    errors,
    warnings,
    totalValueMoved,
  };
}
